import React from 'react';
import Icon from '@/components/Icon';
import { useTheme } from '@/context/ThemeContext';

// Shared chrome for the document and presentation editors. Styling is driven
// by CSS variables set on the editor root so both light and dark app themes
// work without duplicating color logic in every component.
//
// NOTE: <Icon> renders through react-native-fontawesome, which runs colors
// through RN color processing — CSS values like `currentColor` or `var(...)`
// are NOT resolved and silently fall back to black (invisible on dark
// themes). Icons must always receive a concrete theme color.

export const EDITOR_ACCENT = '#3157a4';

export const EDITOR_CHROME_CSS = `
  .gab-editor-root {
    --ed-accent: #3157a4;
  }
  .gab-editor-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    height: 30px;
    min-width: 30px;
    padding: 0 7px;
    border: 0;
    border-radius: 7px;
    background: transparent;
    color: var(--ed-fg);
    font: 600 12px/1 -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
    cursor: pointer;
    transition: background 0.12s ease, color 0.12s ease;
    white-space: nowrap;
  }
  .gab-editor-btn:hover:not(:disabled) { background: var(--ed-hover); }
  .gab-editor-btn:disabled { opacity: 0.38; cursor: default; }
  .gab-editor-btn.is-active { background: var(--ed-active-bg); color: var(--ed-accent); }
  .gab-editor-btn.is-danger:hover:not(:disabled) { background: rgba(220, 38, 38, 0.12); color: #dc2626; }
  .gab-editor-btn-primary {
    display: inline-flex;
    align-items: center;
    gap: 7px;
    height: 34px;
    padding: 0 14px;
    border: 0;
    border-radius: 8px;
    /* Hardcoded fallback — some light-theme contexts leave --ed-accent unset,
       which made the Export label render white-on-white. */
    background: #3157a4;
    background: var(--ed-accent, #3157a4);
    color: #fff;
    font: 700 12.5px/1 -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
    cursor: pointer;
    transition: filter 0.12s ease;
    white-space: nowrap;
  }
  .gab-editor-btn-primary:hover:not(:disabled) { filter: brightness(1.12); }
  .gab-editor-btn-primary:disabled { opacity: 0.55; cursor: default; }
  .gab-editor-select {
    height: 30px;
    padding: 0 8px;
    border: 1px solid var(--ed-border);
    border-radius: 7px;
    background: var(--ed-surface);
    color: var(--ed-fg);
    font: 600 12px/1 -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
    cursor: pointer;
  }
  .gab-editor-divider {
    width: 1px;
    height: 20px;
    margin: 0 5px;
    background: var(--ed-border);
    flex-shrink: 0;
  }
  .gab-editor-toolbar {
    display: flex;
    align-items: center;
    gap: 2px;
    flex-wrap: wrap;
    padding: 6px 10px;
    border-bottom: 1px solid var(--ed-border);
    background: var(--ed-surface);
  }
  .gab-editor-pill {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    height: 22px;
    padding: 0 9px;
    border-radius: 11px;
    font: 600 10.5px/1 -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  }
`;

export function editorChromeVars(colors) {
  return {
    '--ed-fg': colors.text_color_primary,
    '--ed-fg-muted': colors.text_color_secondary,
    '--ed-border': colors.border_color_secondary,
    '--ed-surface': colors.solid_color_primary,
    '--ed-surface-raised': colors.solid_color_block,
    '--ed-hover': 'rgba(128, 128, 128, 0.14)',
    '--ed-active-bg': 'rgba(49, 87, 164, 0.16)',
    '--ed-accent': EDITOR_ACCENT,
  };
}

/** Primary CTA (Export, etc.) — inline colors so light themes can't wash it out. */
export function PrimaryButton({
  children,
  onClick,
  disabled = false,
  className = '',
  style = undefined,
  ...rest
}) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      className={`gab-editor-btn-primary${className ? ` ${className}` : ''}`}
      style={{
        background: EDITOR_ACCENT,
        color: '#fff',
        ...style,
      }}
      {...rest}
    >
      {children}
    </button>
  );
}

export function ToolButton({ icon, label, onPress, active = false, disabled = false, danger = false, text = null, iconSize = 14 }) {
  const { colors } = useTheme();
  const iconColor = active ? EDITOR_ACCENT : colors.text_color_primary;
  return (
    <button
      type="button"
      title={label}
      aria-label={label}
      aria-pressed={active || undefined}
      disabled={disabled}
      onMouseDown={event => event.preventDefault()}
      onClick={onPress}
      className={`gab-editor-btn${active ? ' is-active' : ''}${danger ? ' is-danger' : ''}`}
    >
      {icon ? <Icon name={icon} size={iconSize} color={iconColor} /> : null}
      {text ? <span>{text}</span> : null}
    </button>
  );
}

export function ToolbarDivider() {
  return <span className="gab-editor-divider" aria-hidden="true" />;
}

export function SaveStatusPill({ status }) {
  const { colors } = useTheme();
  const config = {
    saved: { label: 'Saved', color: '#16a34a', bg: 'rgba(22, 163, 74, 0.13)', icon: 'check' },
    saving: { label: 'Saving…', color: '#b45309', bg: 'rgba(217, 119, 6, 0.13)', icon: 'spinner' },
    unsaved: { label: 'Unsaved', color: '#b45309', bg: 'rgba(217, 119, 6, 0.13)', icon: 'pen' },
    error: { label: 'Save failed', color: '#dc2626', bg: 'rgba(220, 38, 38, 0.13)', icon: 'triangle-exclamation' },
  }[status] || { label: status, color: colors.text_color_secondary, bg: 'var(--ed-hover)', icon: 'circle-info' };
  return (
    <span className="gab-editor-pill" style={{ color: config.color, background: config.bg }} role="status">
      <Icon name={config.icon} size={9} color={config.color} />
      {config.label}
    </span>
  );
}
